import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Shuffle, RotateCcw, Play, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import SectionHeader from './SectionHeader.shadcn';

/**
 * Individual drawn card with loading state
 */
function DrawnCard({ card, isScene, getProxyImageUrl }) {
  const [isLoading, setIsLoading] = useState(true);
  
  return (
    <div className={cn(
      "relative flex-shrink-0 rounded-sm overflow-hidden shadow-md",
      "w-[100px] h-[140px]",
      isScene && "rotate-90 my-6 mx-3"
    )}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
      <img
        src={getProxyImageUrl(card.images?.blob?.thumbnail || card.images?.original?.thumbnail)}
        alt={card.name}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-200",
          isLoading ? "opacity-0" : "opacity-100"
        )}
        title={card.name}
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
      />
    </div>
  );
}

DrawnCard.propTypes = {
  card: PropTypes.object.isRequired,
  isScene: PropTypes.bool.isRequired,
  getProxyImageUrl: PropTypes.func.isRequired,
};

/**
 * Draw Simulator sidebar module - simulate drawing hands
 * Using shadcn/ui components with Tailwind
 */
function DrawSimulator({
  isCollapsed,
  onToggle,
  drawnCards,
  remainingDeck,
  hasDrawn,
  mulliganCount,
  deckHasCards,
  onDrawInitialHand,
  onMulligan,
  onResetDraw,
  getProxyImageUrl,
  t = (key) => null,
  hideHeader = false,
}) {
  return (
    <div className="flex flex-col gap-2">
      {!hideHeader && (
        <SectionHeader
          title={t('sidebar.drawSimulator.title') || 'Draw Simulator'}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
        />
      )}

      {!isCollapsed && (
        <>
          {!deckHasCards ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground italic">
              {t('sidebar.drawSimulator.addCardsMessage') || 'Add cards to your deck to use the draw simulator'}
            </p>
          ) : (
            <>
              {!hasDrawn ? (
                <div className="p-2">
                  <Button 
                    onClick={onDrawInitialHand} 
                    className="w-full"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {t('sidebar.drawSimulator.drawHand') || 'Draw Opening Hand'}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Drawn Cards Display */}
                  <div className="px-2">
                    <p className="text-xs font-semibold mb-2">
                      {t('sidebar.drawSimulator.yourHand') || 'Your Hand'} ({drawnCards.length})
                      {mulliganCount > 0 && (
                        <span className="ml-2 text-muted-foreground">
                          ({t('sidebar.drawSimulator.mulligans') || 'Mulligans'}: {mulliganCount})
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center items-center">
                      {drawnCards.map((card, index) => {
                        const isScene = card.feature?.value === 'scene' || card.cardType === 'Scene';
                        return (
                          <DrawnCard
                            key={`${card.id || card.cardNumber}-${index}`}
                            card={card}
                            isScene={isScene}
                            getProxyImageUrl={getProxyImageUrl}
                          />
                        );
                      })}
                    </div>
                    <p className="text-xs text-center text-muted-foreground mt-3">
                      {t('sidebar.drawSimulator.remaining') || 'Remaining'}: {remainingDeck.length}
                    </p>
                  </div>

                  {/* Draw Actions */}
                  <div className="flex gap-2 justify-center p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onMulligan}
                      disabled={remainingDeck.length < 5}
                    >
                      <Shuffle className="h-4 w-4 mr-1" />
                      {t('sidebar.drawSimulator.mulligan') || 'Mulligan'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onResetDraw}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      {t('sidebar.drawSimulator.reset') || 'Reset'}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

DrawSimulator.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  drawnCards: PropTypes.array.isRequired,
  remainingDeck: PropTypes.array.isRequired,
  hasDrawn: PropTypes.bool.isRequired,
  mulliganCount: PropTypes.number.isRequired,
  deckHasCards: PropTypes.bool.isRequired,
  onDrawInitialHand: PropTypes.func.isRequired,
  onMulligan: PropTypes.func.isRequired,
  onResetDraw: PropTypes.func.isRequired,
  getProxyImageUrl: PropTypes.func.isRequired,
  t: PropTypes.func,
  hideHeader: PropTypes.bool,
};

export default DrawSimulator;
